import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { FileAudio, FileText, ImageIcon, Play, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ComponentType } from 'react';
import { useMemo } from 'react';
import { ProductLineSubject } from '@/app/components/icons/ProductLineSubject';
import { SuggestLineReport } from '@/app/components/icons/SuggestLineReport';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MENTION_COLOR_MAP } from '@/app/constants';
import { cn } from '@/app/utils';
import { useMentionContext } from '../context';

// 扩展NodeViewProps接口以包含自定义选项
interface MentionNodeViewProps extends Omit<NodeViewProps, 'extension'> {
  extension: NodeViewProps['extension'] & {
    options: {
      showTooltip: boolean;
      className?: string;
      variant?: 'default' | 'media-chip';
    };
  };
}

const MentionNodeView = ({ node, extension }: MentionNodeViewProps) => {
  const t = useTranslations('create.proMode');
  const { getPrefix } = useMentionContext();
  const showTooltip = extension.options.showTooltip;
  const mentionClassName = extension.options.className;
  const variant = extension.options.variant ?? 'default';

  // 提取 preview 和 mediaType 到组件作用域
  const preview = typeof node.attrs.preview === 'string' ? node.attrs.preview : '';
  const mediaType = typeof node.attrs.mediaType === 'string' ? node.attrs.mediaType : '';

  const prefix = useMemo(() => {
    if (variant === 'media-chip') {
      if (preview && mediaType === 'image') {
        return (
          <span className="mr-1 flex size-4 overflow-hidden rounded-4 bg-black/20 ring-1 ring-white/20">
            <img src={preview} alt="" className="size-full object-cover" />
          </span>
        );
      }

      if (preview && mediaType === 'video') {
        return (
          <span className="relative mr-1 flex size-4 overflow-hidden rounded-4 bg-black/20 ring-1 ring-white/20">
            <img src={preview} alt="" className="size-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
              <Play className="size-2 fill-current" />
            </span>
          </span>
        );
      }

      const contextPrefix = getPrefix?.(node.attrs.id, node.attrs.label);
      if (contextPrefix) return contextPrefix;

      let Icon: ComponentType<{ className?: string }> = FileText;
      if (mediaType === 'element') Icon = ProductLineSubject;
      else if (mediaType === 'image') Icon = ImageIcon;
      else if (mediaType === 'video') Icon = Video;
      else if (mediaType === 'audio') Icon = FileAudio;

      return (
        <span className="mr-1 inline-flex size-5 items-center justify-center rounded-full bg-white/15 text-[#9EC1FF]">
          <Icon className="size-3.5" />
        </span>
      );
    }

    // 默认 variant：缩略图圆角从 2px 改为 4px
    if (preview) {
      if (mediaType === 'video') {
        return (
          <span className="relative mr-0.5 flex size-4 shrink-0 overflow-hidden rounded-[4px] bg-black/20">
            <img src={preview} alt="" className="size-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
              <Play className="size-2 fill-current" />
            </span>
          </span>
        );
      }

      return (
        <span className="mr-0.5 flex size-4 shrink-0 overflow-hidden rounded-[4px] bg-black/20">
          <img src={preview} alt="" className="size-full object-cover" />
        </span>
      );
    }

    if (getPrefix) {
      return getPrefix(node.attrs.id, node.attrs.label);
    }
    return null;
  }, [getPrefix, node.attrs.id, node.attrs.label, preview, mediaType, variant]);

  const alignMiddle = !!prefix;

  // 构建 tooltip 内容
  const tooltipContent = useMemo(() => {
    if (showTooltip) return t('mentionTip');

    // 如果有 preview，显示图片
    if (preview) {
      if (mediaType === 'video') {
        return (
          <div className="relative w-48 overflow-hidden rounded-lg">
            <img src={preview} alt={node.attrs.label} className="w-full" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
              <Play className="size-6 fill-current" />
            </span>
          </div>
        );
      }
      return <img src={preview} alt={node.attrs.label} className="w-48 rounded-lg" />;
    }

    return '';
  }, [showTooltip, preview, mediaType, node.attrs.label, t]);

  return (
    <NodeViewWrapper
      as="span"
      data-drag-handle
      className={cn('mention mx-0.5 cursor-grab', (showTooltip || alignMiddle) && 'align-middle')}
    >
      <CustomTooltip
        content={tooltipContent}
        variant={preview ? 'glass' : undefined}
        contentClassName={preview ? 'p-1' : undefined}
        disableHoverableContent={true}
      >
        <span
          className={cn(
            'group relative inline-flex items-center gap-1 rounded-6 py-0.5 pr-1.5 pl-1 font-normal text-[14px] leading-5',
            variant === 'media-chip' && 'rounded-10 px-2 py-0.5',
            mentionClassName,
            node.attrs.color === MENTION_COLOR_MAP.forbidden ? 'text-system-white64' : '',
          )}
          style={
            {
              whiteSpace: 'nowrap',
              background:
                variant === 'media-chip' ? 'rgba(80,141,255,0.10)' : showTooltip ? '#333333' : 'rgba(0, 0, 0, 0.4)',
            } as React.CSSProperties
          }
        >
          {showTooltip && <SuggestLineReport className="shrink-0 text-base text-system-white" />}
          {prefix}
          <span className="opacity-[0.64] transition-opacity group-hover:opacity-100">{node.attrs.label}</span>
        </span>
      </CustomTooltip>
    </NodeViewWrapper>
  );
};

export default MentionNodeView;
